import { siteData } from "../copy";
import { useNarrow } from "./useNarrow";

/**
 * The maps analogy (the prototype's mapRoute): three routes from "where you are" to "your
 * priorities", with Abi's highlighted. Redrawn for narrow screens. Keeps its light surface on the
 * dark page on purpose.
 */
export function MapRoute() {
  const narrow = useNarrow(780, 220);
  const R = siteData<[string, string][]>("home.maps.routes");
  const W = narrow ? 460 : 960;
  const H = narrow ? 560 : 340;
  const grid: { x1: number; y1: number; x2: number; y2: number; w: number }[] = [];
  if (narrow) {
    for (let i = 0; i < 5; i++)
      grid.push({ x1: 40 + i * 110, y1: 0, x2: 10 + i * 110, y2: H, w: 14 });
    for (let i = 0; i < 6; i++)
      grid.push({ x1: 0, y1: 60 + i * 92, x2: W, y2: 60 + i * 92, w: 14 });
  } else {
    for (let i = 0; i < 7; i++)
      grid.push({ x1: 60 + i * 130, y1: 0, x2: 10 + i * 130, y2: 340, w: 12 });
    for (let i = 0; i < 4; i++)
      grid.push({ x1: 0, y1: 50 + i * 86, x2: 960, y2: 50 + i * 86, w: 12 });
  }
  const P = narrow
    ? {
        A: "M96 452 C 40 330 150 250 300 128",
        B: "M96 452 C 250 420 330 330 300 128",
        C: "M96 452 C 170 380 196 250 300 128",
        lA: [236, 262],
        lB: [244, 392],
        lC: [232, 322],
        s: [96, 452],
        e: [300, 128],
        fs: 12.5,
        pw: [262, 276, 292],
      }
    : {
        A: "M120 252 C 250 84 430 56 828 92",
        B: "M120 252 C 310 318 580 300 828 92",
        C: "M120 252 C 320 238 500 152 828 92",
        lA: [380, 96],
        lB: [462, 292],
        lC: [448, 176],
        s: [120, 252],
        e: [828, 92],
        fs: 12.5,
        pw: [252, 268, 302],
      };
  const pill = (x: number, y: number, w: number, txt: string, dark: boolean, fs: number) => (
    <g transform={`translate(${x},${y})`}>
      <rect
        x={-w / 2}
        y={-17}
        width={w}
        height={dark ? 34 : 32}
        rx={16}
        fill={dark ? "#0B2A5B" : "#fff"}
        stroke={dark ? undefined : "#DED4C2"}
      />
      <circle cx={-w / 2 + 18} cy={dark ? 0 : -1} r={4.5} fill={dark ? "#F7C733" : "#BF4F78"} />
      <text
        x={-w / 2 + 31}
        y={4}
        fontSize={fs}
        fontFamily="Inter,sans-serif"
        fontWeight={dark ? 600 : undefined}
        fill={dark ? "#fff" : "#6B7A91"}
      >
        {txt}
      </text>
    </g>
  );
  const anchor = narrow ? "start" : "middle";
  return (
    <div id="maproute">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Three routes from where your money is today to your priorities, with the one Abi picks highlighted"
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          borderRadius: 16,
          background: "#FBF7EF",
        }}
      >
        {grid.map((g, i) => (
          <line
            key={i}
            x1={g.x1}
            y1={g.y1}
            x2={g.x2}
            y2={g.y2}
            stroke="#0B2A5B"
            strokeOpacity=".05"
            strokeWidth={g.w}
          />
        ))}
        <path
          d={P.A}
          fill="none"
          stroke="#9AA7BB"
          strokeWidth={3}
          strokeDasharray="7 7"
          strokeLinecap="round"
        />
        <path
          d={P.B}
          fill="none"
          stroke="#9AA7BB"
          strokeWidth={3}
          strokeDasharray="7 7"
          strokeLinecap="round"
        />
        <path
          id="map-route-abi"
          d={P.C}
          fill="none"
          stroke="#2E6FBF"
          strokeWidth={5.5}
          strokeLinecap="round"
        />
        <circle r={7} fill="#2E6FBF">
          <animateMotion
            dur="4.6s"
            repeatCount="indefinite"
            calcMode="spline"
            keyPoints="0;1"
            keyTimes="0;1"
            keySplines="0.45 0 0.3 1"
          >
            <mpath href="#map-route-abi" />
          </animateMotion>
          <animate attributeName="opacity" values="0;1;1;1;0" dur="4.6s" repeatCount="indefinite" />
        </circle>
        {pill(P.lA[0], P.lA[1], P.pw[0], R[0]?.[0] ?? "", false, P.fs)}
        {pill(P.lB[0], P.lB[1], P.pw[1], R[1]?.[0] ?? "", false, P.fs)}
        {pill(P.lC[0], P.lC[1], P.pw[2], R[2]?.[0] ?? "", true, P.fs + 0.5)}
        <g transform={`translate(${P.s[0]},${P.s[1]})`}>
          <circle r={17} fill="#fff" stroke="#0B2A5B" strokeWidth={2.5} />
          <circle r={6} fill="#0B2A5B" />
          <text
            y={44}
            textAnchor={anchor}
            x={narrow ? -14 : 0}
            fontSize={10.5}
            fontFamily="Roboto Mono,monospace"
            letterSpacing={1.6}
            fill="#78879F"
          >
            WHERE YOU ARE
          </text>
          <text
            y={63}
            textAnchor={anchor}
            x={narrow ? -14 : 0}
            fontSize={14}
            fontFamily="Inter,sans-serif"
            fontWeight={600}
            fill="#0F2444"
          >
            $533,903 today
          </text>
        </g>
        <g transform={`translate(${P.e[0]},${P.e[1]})`}>
          <path d="M0 6 L0 -36" stroke="#0B2A5B" strokeWidth={2.5} strokeLinecap="round" />
          <path d="M0 -36 L34 -28 L0 -19 Z" fill="#E8A90B" />
          <circle r={6} fill="#0B2A5B" />
          <text
            y={-52}
            textAnchor="middle"
            fontSize={10.5}
            fontFamily="Roboto Mono,monospace"
            letterSpacing={1.6}
            fill="#78879F"
          >
            WHERE YOU WANT TO GO
          </text>
          <text
            y={-72}
            textAnchor="middle"
            fontSize={14}
            fontFamily="Inter,sans-serif"
            fontWeight={600}
            fill="#0F2444"
          >
            Your priorities
          </text>
        </g>
      </svg>
    </div>
  );
}
